import { destroyCurrentUser, getCurrentUser, getTodayStats, updateUserProfile } from '@/lib/api/currentUserApi'
import {
  GetCurrentUserQuery,
  UpdateUserProfileMutation,
  UpdateUserProfileMutationVariables,
  UserRole,
  type DestroyCurrentUserMutationVariables,
  type GetTodayStatsQuery,
} from '@/typings/domain'
import { createAsyncThunk, createSlice } from '@reduxjs/toolkit'
import type { RootState } from '../store'
import type { QueryState } from './queryStateType'
import { logoutThunk } from './sessionSlice'

export type CurrentUser = NonNullable<GetCurrentUserQuery['currentUser']>
export type BillingAccountUser = NonNullable<CurrentUser['billingAccountUsers']>[number]
export type TodayStats = NonNullable<GetTodayStatsQuery['currentUser']>['stats']

type CurrentUserState = {
  user: CurrentUser | null
  todayStats: TodayStats | null
  isOwner: boolean
  isOwnerOrAdmin: boolean
  isWorker: boolean
} & QueryState

const initialState: CurrentUserState = {
  user: null,
  todayStats: null,
  isOwner: false,
  isOwnerOrAdmin: false,
  isWorker: false,
  isLoading: false,
  loaded: false,
  error: null,
}

export const currentUserSlice = createSlice({
  name: 'currentUser',
  initialState,
  reducers: {
    resetCurrentUser: state => {
      state.user = null
      state.loaded = false
      state.error = null
    },
  },
  extraReducers: builder => {
    builder
      .addCase(fetchCurrentUserThunk.fulfilled, (state, action) => {
        state.user = action.payload
        state.isOwner = action.payload.role === UserRole.Owner
        state.isOwnerOrAdmin = action.payload.role === UserRole.Owner || action.payload.role === UserRole.Admin
        state.isWorker = action.payload.role === UserRole.Worker
        state.loaded = true
        state.error = null
      })
      .addCase(fetchCurrentUserThunk.rejected, state => {
        state.user = null
        state.loaded = true
        state.error = 'Not signed in'
      })
      .addCase(fetchTodayStatsThunk.fulfilled, (state, action) => {
        state.todayStats = action.payload
      })
      .addCase(updateUserProfileThunk.fulfilled, (state, action) => {
        if (state.user) {
          state.user = {
            ...state.user,
            ...action.payload,
          }
        }
      })
      .addCase(logoutThunk.fulfilled, () => initialState)
  },
})

export const fetchCurrentUserThunk = createAsyncThunk<CurrentUser>('currentUser/fetch', async (_, thunkAPI) => {
  const { data } = await getCurrentUser()
  return data.currentUser ?? thunkAPI.rejectWithValue({ message: 'Not signed in' })
})

export const fetchTodayStatsThunk = createAsyncThunk('currentUser/todayStats', async (_, thunkAPI) => {
  const { data } = await getTodayStats()
  return data.currentUser?.stats ?? thunkAPI.rejectWithValue({ message: 'Not signed in' })
})

export const updateUserProfileThunk = createAsyncThunk<
  NonNullable<NonNullable<UpdateUserProfileMutation['updateProfile']>['user']>,
  UpdateUserProfileMutationVariables['input']
>('currentUser/updateProfile', async (payload, thunkAPI) => {
  const { data } = await updateUserProfile(payload)
  const errors = data?.updateProfile?.errors ?? []
  return data?.updateProfile?.user ?? thunkAPI.rejectWithValue(errors)
})

export const destroyCurrentUserThunk = createAsyncThunk<void, DestroyCurrentUserMutationVariables['input']>(
  'currentUser/destroyCurrentUser',
  async (payload, thunkAPI) => {
    const { data } = await destroyCurrentUser(payload)
    const errors = data?.destroyUser?.errors
    if (errors) {
      return thunkAPI.rejectWithValue(errors)
    }
  }
)

export const selectCurrentBillingAccount = (state: RootState) => state.currentUser.user?.billingAccount ?? null

export const { resetCurrentUser } = currentUserSlice.actions

export default currentUserSlice.reducer
